import { useEffect, useState } from 'react';
import { formatCurrency } from '../../lib/format';
import { setWasserijItemKorting } from '../../lib/orders';
import {
  addWasserijItemBehandeling,
  fetchWasserijItemBehandelingCatalog,
  type BehandelingCatalog,
  type BehandelingCatalogItem,
} from '../../lib/wasserij-item-behandeling';

/** Korting-snelkeuzes bij toevoegen. 100 = gratis weggeven. */
const KORTING_KEUZES: { pct: number; label: string }[] = [
  { pct: 0, label: 'Geen korting' },
  { pct: 25, label: '25% korting' },
  { pct: 50, label: '50% korting' },
  { pct: 100, label: 'Gratis' },
];

interface WasserijItemBehandelingDialogProps {
  isOpen: boolean;
  parentItemId: string | null;
  parentName?: string;
  onClose: () => void;
  onAdded: () => void;
}

export function WasserijItemBehandelingDialog({
  isOpen,
  parentItemId,
  parentName,
  onClose,
  onAdded,
}: WasserijItemBehandelingDialogProps) {
  const [catalog, setCatalog] = useState<BehandelingCatalog | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !parentItemId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCatalog(null);
    fetchWasserijItemBehandelingCatalog(parentItemId)
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
        if (!data.success && data.message) setError(data.message);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Catalog laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, parentItemId]);

  if (!isOpen || !parentItemId) return null;

  const availableItems = (catalog?.items ?? []).filter((i) => !i.alreadyLinked);

  async function handleAdd(item: BehandelingCatalogItem, kortingPct: number) {
    if (!parentItemId) return;
    setBusyCode(item.sku);
    setError(null);
    try {
      const res = await addWasserijItemBehandeling({
        parentItemId,
        behandelingCode: item.sku,
      });
      if (!res.success) {
        setError(res.message || 'Toevoegen mislukt');
        return;
      }
      // Korting is een tweede mutatie op het verse child-record. Faalt die, dan
      // staat de behandeling er wel (vol tarief) — laat de dialog open met een
      // duidelijke melding zodat de gebruiker 'm op de regel kan bijstellen.
      if (kortingPct > 0 && res.childId) {
        try {
          await setWasserijItemKorting(res.childId, kortingPct);
        } catch {
          setError(
            'Behandeling toegevoegd, maar de korting kon niet worden toegepast. Sluit dit venster en stel de korting in op de regel.'
          );
          return;
        }
      }
      onAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setBusyCode(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Behandeling toevoegen</h3>
            {(catalog?.parentName || parentName) && (
              <p className="text-xs text-slate-500 mt-0.5">
                Voor: {catalog?.parentName || parentName}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">
              {error}
            </div>
          )}

          {!loading && catalog?.success && availableItems.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6">
              Alle behandelingen zijn al gekoppeld.
            </p>
          )}

          {!loading && availableItems.length > 0 && (
            <ul className="space-y-2">
              {availableItems.map((item) => (
                <BehandelingRow
                  key={item.sku}
                  item={item}
                  busy={busyCode !== null}
                  isThisBusy={busyCode === item.sku}
                  onAdd={handleAdd}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Eén catalogus-rij met een korting-keuze. Default "Geen korting"; bij toevoegen
 * wordt de gekozen korting (incl. 100% = gratis) direct op de nieuwe regel gezet.
 */
function BehandelingRow({
  item,
  busy,
  isThisBusy,
  onAdd,
}: {
  item: BehandelingCatalogItem;
  busy: boolean;
  isThisBusy: boolean;
  onAdd: (item: BehandelingCatalogItem, kortingPct: number) => void;
}) {
  const [kortingPct, setKortingPct] = useState(0);
  const base = typeof item.unitPriceInclVat === 'number' ? item.unitPriceInclVat : 0;
  const effective = kortingPct > 0 ? base * (1 - kortingPct / 100) : base;

  return (
    <li className="flex items-start gap-3 border border-slate-200 rounded-lg p-3 hover:border-indigo-300">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{item.name}</div>
        {item.description && (
          <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
        )}
        {base > 0 && (
          <p className="text-xs text-slate-600 mt-1">
            {kortingPct > 0 ? (
              <>
                <span className="line-through text-slate-400">
                  + {formatCurrency(base)}
                </span>{' '}
                <span className="font-medium text-emerald-700">
                  {effective === 0 ? 'Gratis' : `+ ${formatCurrency(effective)}`} incl. btw
                </span>
              </>
            ) : (
              <>+ {formatCurrency(base)} incl. btw</>
            )}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <select
          value={kortingPct}
          onChange={(e) => setKortingPct(Number(e.target.value))}
          disabled={busy}
          className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white disabled:opacity-60"
          aria-label="Korting"
        >
          {KORTING_KEUZES.map((k) => (
            <option key={k.pct} value={k.pct}>
              {k.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy}
          onClick={() => onAdd(item, kortingPct)}
          className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300 w-full"
        >
          {isThisBusy ? 'Bezig…' : kortingPct === 100 ? 'Gratis toevoegen' : 'Toevoegen'}
        </button>
      </div>
    </li>
  );
}
